import { useState, useEffect, useRef } from "react";
import { useNavigate, useNavigation, useRevalidator } from "@remix-run/react";
import { useApi } from "@core/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCheck, faCircle } from "@fortawesome/pro-light-svg-icons";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { clearSiteTopBannerHeight, setSiteTopBannerHeight } from "./siteTopBanners";

export default function AccountCompletionBanner() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  const dismissKey = user?.Id ? `accountCompletionBannerDismissed:${user.Id}` : null;

  useEffect(() => {
    if (!dismissKey) { setIsDismissed(false); return; }
    setIsDismissed(localStorage.getItem(dismissKey) === "true");
  }, [dismissKey]);

  const { data: steps } = useQuery({
    queryKey: ["account-completion-steps", user?.Id],
    queryFn: () => api["/site/account-completion-steps"](),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    enabled: !!user?.Id,
  });

  // Re-check steps whenever a navigation, form submit or loader revalidation finishes
  // (wallet reload, patient created, order placed all go through one of these).
  const routerBusy = navigation.state !== "idle" || revalidator.state !== "idle";
  const wasBusyRef = useRef(false);
  useEffect(() => {
    if (routerBusy) {
      wasBusyRef.current = true;
      return;
    }
    if (wasBusyRef.current) {
      wasBusyRef.current = false;
      queryClient.invalidateQueries({ queryKey: ["account-completion-steps"] });
    }
  }, [routerBusy, queryClient]);

  // Same for react-query data that backs wallet / patients / orders.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      if (event.type !== "updated" || event.action.type !== "success") return;
      if (event.query.state.dataUpdateCount < 2) return;
      if (!/payment|wallet|patient|order/i.test(String(event.query.queryKey[0] ?? ""))) return;
      clearTimeout(timer);
      timer = setTimeout(
        () => queryClient.invalidateQueries({ queryKey: ["account-completion-steps"] }),
        400,
      );
    });
    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, [queryClient]);

  const handleDismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, "true");
    setIsDismissed(true);
  };

  // EPCS has its own dedicated banner (EpcsAuthorizationBanner) — do not also
  // surface it here or both bars show the same "go to account settings" action.
  const sorted = (steps ?? [])
    .filter(s => s.IsEnabled && s.Id !== "epcs-authorization")
    .sort((a, b) => a.SortOrder - b.SortOrder);
  const nextStep = sorted.find(s => !s.IsCompleted);
  const doneCount = sorted.filter(s => s.IsCompleted).length;
  const show = !isDismissed && !!nextStep;

  // Register height into the shared siteTopBanners system so the site header shifts down
  useEffect(() => {
    if (!show) {
      clearSiteTopBannerHeight("onboarding");
      return;
    }
    const updateOffset = () => {
      setSiteTopBannerHeight("onboarding", bannerRef.current?.offsetHeight ?? 0);
    };
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    if (bannerRef.current) observer.observe(bannerRef.current);
    window.addEventListener("resize", updateOffset);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateOffset);
      clearSiteTopBannerHeight("onboarding");
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      ref={bannerRef}
      data-testid="account-completion-banner"
      className="fixed left-0 right-0 flex items-center justify-between px-4 py-1.5 print:hidden"
      style={{ top: "calc(var(--site-top-banner-below-account, 0px) + var(--site-top-banner-announcement-height, 0px))", zIndex: 249, background: "#0a1628", borderBottom: "1px solid #1a2e4a" }}
    >
      <span className="text-sm font-semibold tracking-widest uppercase" style={{ color: "#D4AF37", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.12em" }}>
        Onboarding
      </span>
      <span className="mx-3 text-sm" style={{ color: "rgba(255,255,255,0.7)", fontFamily: "'Outfit', sans-serif" }}>
        {doneCount + 1} of {sorted.length}: {nextStep.Name}
      </span>
      <div className="hidden md:flex items-center gap-3 ml-2">
        {sorted.map(s => (
          <button
            key={s.Id}
            type="button"
            onClick={() => navigate(s.Route)}
            className="flex items-center gap-1 text-xs"
            style={{
              color: s.IsCompleted ? "#66AF4E" : s.Id === nextStep.Id ? "#D4AF37" : "rgba(255,255,255,0.5)",
              background: "none",
              border: "none",
              fontFamily: "'Outfit', sans-serif",
            }}
          >
            <FontAwesomeIcon icon={s.IsCompleted ? faCheck : faCircle} />
            {s.Name}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <button
          type="button"
          onClick={() => navigate(nextStep.Route)}
          className="px-3 py-1 rounded text-xs font-semibold"
          style={{ background: "#D4AF37", color: "#0F2942", border: "none", fontFamily: "'Outfit', sans-serif" }}
        >
          Go
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1"
          style={{ color: "rgba(255,255,255,0.5)" }}
          aria-label="Dismiss"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
    </div>
  );
}
